export type PulseCacheKey =
  | 'pulse:checks'
  | 'pulse:tracks'
  | 'pulse:integrations'
  | `pulse:catalog-groups:${string}`
  | `pulse:check-details:${string}`
  | `pulse:check-history:${string}:${number}`
  | `pulse:resolved-values:${string}:${string}`
  | `pulse:track-checks:${string}`
  | `pulse:track-details:${string}`
  | `pulse:track-history:${string}:${number}`
  | `pulse:insights:${string}`
  | `pulse:integration-health:${string}`
  | `pulse:fact-collectors-all:${string}`
  | `pulse:fact-collectors:${string}`;

export type PulseCacheKeyPrefix =
  | PulseCacheKey
  | `pulse:check-history:${string}:`
  | `pulse:resolved-values:${string}:`
  | 'pulse:track-checks:'
  | 'pulse:track-details:'
  | 'pulse:track-history:'
  | 'pulse:insights:'
  | 'pulse:integration-health:';

export const PULSE_CACHE_KEYS = {
  checks: 'pulse:checks',
  tracks: 'pulse:tracks',
  integrations: 'pulse:integrations',
  catalogGroups: (userEntityRef: string) =>
    `pulse:catalog-groups:${userEntityRef}` as const,
  checkDetails: (checkId: string) => `pulse:check-details:${checkId}` as const,
  checkHistory: (checkId: string, days: number) =>
    `pulse:check-history:${checkId}:${days}` as const,
  resolvedValues: (checkId: string, entityRef: string) =>
    `pulse:resolved-values:${checkId}:${entityRef}` as const,
  trackChecks: (checkIds: string[], entityRefs?: string[]) => {
    const normalizedCheckIds = [...new Set(checkIds)].sort((left, right) =>
      left.localeCompare(right),
    );
    const normalizedEntityRefs = entityRefs
      ? [...new Set(entityRefs)].sort((left, right) =>
          left.localeCompare(right),
        )
      : null;
    return `pulse:track-checks:${JSON.stringify([
      normalizedCheckIds,
      normalizedEntityRefs,
    ])}` as const;
  },
  trackDetails: (trackId: string) => `pulse:track-details:${trackId}` as const,
  trackHistory: (trackId: string, days: number) =>
    `pulse:track-history:${trackId}:${days}` as const,
  insights: (ownerScopeKey: string) =>
    `pulse:insights:${ownerScopeKey}` as const,
  integrationHealth: (integrationId: string) =>
    `pulse:integration-health:${integrationId}` as const,
  allFactCollectors: (integrationIdsKey: string) =>
    `pulse:fact-collectors-all:${integrationIdsKey}` as const,
  factCollectors: (integrationId: string) =>
    `pulse:fact-collectors:${encodeURIComponent(integrationId)}` as const,
} as const satisfies Record<
  string,
  PulseCacheKey | ((...args: never[]) => PulseCacheKey)
>;

export const PULSE_CACHE_KEY_PREFIXES = {
  checkHistory: (checkId: string) => `pulse:check-history:${checkId}:` as const,
  resolvedValues: (checkId: string) =>
    `pulse:resolved-values:${checkId}:` as const,
  trackChecks: 'pulse:track-checks:',
  trackDetails: 'pulse:track-details:',
  trackHistory: 'pulse:track-history:',
  insights: 'pulse:insights:',
  integrationHealth: 'pulse:integration-health:',
} as const satisfies Record<
  string,
  PulseCacheKeyPrefix | ((...args: never[]) => PulseCacheKeyPrefix)
>;
